/** @file Two-bone shoulder angle in the parent's rotation-0 frame: soft reach, clamp, elbow solve. */
import { angle } from '@pose2d/math/angle';
import { clamp } from '@pose2d/math/clamp';
import { trig } from '@pose2d/math/trig';
import type { Affine } from '@pose2d/math/types';
import { elbowScan } from './elbowScan';
import { elbowSpan } from './elbowSpan';

/** Relative tolerance for "the parent's local frame is a similarity" (equal-length, orthogonal axes). */
const SIMILAR_EPS = 1e-9;
/** [min reach, max reach, target reach, |psi|] scratch (elbowScan's io record; the similarity case uses [0], [1]). */
const RANGE = new Float64Array(4);

/** True when m's linear part maps circles to circles, so the child length does not depend on the elbow angle. */
function isSimilarity(m: Affine): boolean {
  const a = m[0] ?? 0;
  const b = m[1] ?? 0;
  const c = m[2] ?? 0;
  const d = m[3] ?? 0;
  const x = a * a + c * c;
  const y = b * b + d * d;
  const tol = SIMILAR_EPS * (x + y);
  return Math.abs(x - y) <= tol && Math.abs(a * b + c * d) <= tol;
}

/** Soft-IK remap (spec §6.5): past reach - s the distance eases toward reach without getting there. */
function softReach(d: number, reach: number, softness: number): number {
  const s = softness < reach ? softness : reach;
  if (!(s > 0)) return d;
  const start = reach - s;
  return d > start ? start + s * (1 - Math.exp(-(d - start) / s)) : d;
}

/** Exterior elbow angle in [0, pi] (0 = straight) of a triangle with sides l1, l2 and dd (law of cosines). */
function lawOfCosines(l1: number, l2: number, dd: number): number {
  const c = clamp((dd * dd - l1 * l1 - l2 * l2) / (2 * l1 * l2), -1, 1);
  return trig.atan2(Math.sqrt(1 - c * c), c);
}

/**
 * Two-bone solve for a target at distance d from the parent joint, in the frame of the parent's local linear part
 * `m` at rotation 0 (child joint e = (ex, ey), child bone of signed length childLen; |e| > 0 and a non-zero child
 * span are the caller's guard). bend +1 bends the elbow counter-clockwise in this frame. Writes out[0] = shoulder
 * angle beta in degrees (the e axis ends at targetAngle - beta), out[1] = reached distance (soft-remapped, clamped),
 * out[2] = full reach (the longest chain); returns out.
 */
export function twoBoneAngles(out: Float64Array, m: Affine, ex: number, ey: number, childLen: number, d: number, softness: number, bend: number): Float64Array {
  const l1 = trig.hypot(ex, ey);
  const similar = isSimilarity(m);
  if (similar) {
    const l2 = elbowSpan(m, ex, ey, childLen, 0);
    RANGE[0] = Math.abs(l1 - l2);
    RANGE[1] = l1 + l2;
  } else {
    elbowScan.range(RANGE, m, ex, ey, childLen, bend);
  }
  const lo = RANGE[0] ?? 0;
  const hi = RANGE[1] ?? 0;
  const dd = clamp(softReach(d, hi, softness), lo, hi);
  RANGE[2] = dd;
  const psi = bend * (similar ? lawOfCosines(l1, hi - l1, dd) : (elbowScan.root(RANGE, m, ex, ey, childLen, bend)[3] ?? 0));
  const span = elbowSpan(m, ex, ey, childLen, psi);
  out[0] = angle.radToDeg(trig.atan2(span * trig.sin(psi), l1 + span * trig.cos(psi)));
  out[1] = dd;
  out[2] = hi;
  return out;
}
